import { useSplitEditorState } from '@telecom/shared/components/spilt-editor/composables/use-split-editor-state';
import { useSpecificationEmbodimentOutlines, useSpecificationEmbodimentConfigRuleTransform, type EmbodimentOutlineConfigRule } from '../../../composables/use-specification-embodiment-outline';
import { computed, ref, onMounted, shallowRef } from 'vue';
import type { Paragraph } from '@telecom/shared/components/paragraph-editor/type';
import type { EmbodimentOutline } from '@/domains/specification/apis/create-embodiment-outline-api';
import { SpecificationEmbodimentOutlineService } from '@/domains/specification/services/specification-embodiment-outline-service';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import type { SpecificationParagraphWithData } from '@/domains/specification/types/specification-gen-content';
import { SpecificationEmbodimentImprovementModel } from '../models/specification-embodiment-improvement-model';
import { useDisposes } from '@telecom/shared/composables/use-disposes';
import { SpecificationContentType } from '@/domains/specification/enums';
import { StreamStatus } from '@/services/sse-ps-http/enums';
import { SpecificationGenContentService } from '@/domains/specification/services/specification-gen-content-service';
import _ from 'lodash';

const useImprovementModel = () => {
    const improvementModel = injectService<SpecificationEmbodimentImprovementModel>(SpecificationEmbodimentImprovementModel);
    const _modifiedOutline = shallowRef<EmbodimentOutline | undefined>();

    const { disposes } = useDisposes();

    disposes.push(
        improvementModel.onStatesChange(state => _modifiedOutline.value = state.modifiedOutline),
    );

    const modifiedOutline = computed({
        get() {
            return _modifiedOutline.value;
        },
        set(value) {
            improvementModel.update(state => ({ ...state, modifiedOutline: value }));
        },
    });

    return {
        modifiedOutline,
        improvementModel,
    };
};

export const useEmbodimentImprovement = () => {
    const { editingParagraph } = useSplitEditorState<Paragraph<string, SpecificationParagraphWithData<{ embodiment_number: number }>>>();
    const { outlines, model, createConfigRule } = useSpecificationEmbodimentOutlines();
    const { outline2ConfigRule, configRule2Outline } = useSpecificationEmbodimentConfigRuleTransform();
    const outlineService = injectService<SpecificationEmbodimentOutlineService>(SpecificationEmbodimentOutlineService);
    const { improvementModel, modifiedOutline } = useImprovementModel();
    const genContentService = injectService<SpecificationGenContentService>(SpecificationGenContentService);

    const beforeGenerateLoading = ref(false);

    const originalConfigRule = ref<EmbodimentOutlineConfigRule | undefined>();
    const configRule = ref<EmbodimentOutlineConfigRule | undefined>();

    const isAppendingMode = computed(() => {
        return !editingParagraph.value?.raw?.data?.embodiment_number;
    });

    const outline = computed(() => {
        const num = editingParagraph.value?.raw?.data?.embodiment_number;
        return num ? outlines.value.find(outline => outline.embodiment_number === num) : undefined;
    });

    onMounted(() => {
        originalConfigRule.value = outline.value ? outline2ConfigRule(outline.value) : createConfigRule();
        configRule.value = outline.value ? outline2ConfigRule(outline.value) : createConfigRule();

        improvementModel.update(state => ({
            ...state,
            improvingOutlineNumber: _.toNumber(configRule.value?.embodiment_number),
        }));

        modifiedOutline.value = undefined;
        beforeGenerateLoading.value = false;
    });

    const modified = computed(() => {
        return !_.isEqual(originalConfigRule.value, configRule.value);
    });


    const generate = async () => {
        beforeGenerateLoading.value = true;

        modifiedOutline.value = undefined;

        if (configRule.value) {
            // 实施例规则变动时要重新生成大纲
            if (modified.value) {
                const appended = await outlineService.append(configRule2Outline(configRule.value!));
                const newOutline: EmbodimentOutline = {
                    ...appended,
                    type: configRule.value.type,
                    reference_claim_number: [],
                    embodiment_number: _.toNumber(configRule.value.embodiment_number),
                };
                modifiedOutline.value = newOutline;
                configRule.value = outline2ConfigRule(newOutline);
                originalConfigRule.value = outline2ConfigRule(newOutline);
            } else {
                modifiedOutline.value = outline.value;
            }
        }

        beforeGenerateLoading.value = false;
    };

    const validated = computed(() => {
        return configRule.value && configRule.value.claims.length && configRule.value.type && configRule.value.embodiment_number;
    });

    const comfirm = async () => {
        if (!modifiedOutline.value) return;
        const index = model.modelData.outlines.findIndex(o => o.embodiment_number === modifiedOutline.value!.embodiment_number);
        const outlines = [...model.modelData.outlines];
        if (index >= 0) {
            outlines[index] = modifiedOutline.value;
        } else {
            outlines.push(modifiedOutline.value);
        }
        model.update(data => ({ ...data, outlines }));
        await outlineService.save();

        // 追加模式下，需要将当前段落设置为新增的实施例段落
        // 这里伪造一个段落，用于后续的编辑
        if (isAppendingMode.value) {
            const paragraph = {
                type: SpecificationContentType.EmbodimentItem,
                content: modifiedOutline.value.text || '',
                status: StreamStatus.FINISH,
                data: modifiedOutline.value,
            } as SpecificationParagraphWithData<EmbodimentOutline>;
            editingParagraph.value = {
                content: modifiedOutline.value.text || '',
                paragraphType: SpecificationContentType.EmbodimentItem,
                raw: paragraph,
            } as Paragraph;
            try {
                const state = _.cloneDeep(genContentService.getState());
                const generatedContent = state.generatedContent;
                const outlineSection = generatedContent.content.find(p => p.type === SpecificationContentType.EmbodimentOutline)!;
                const subSections = outlineSection.children || [];
                const conclusionIndex = subSections.findLastIndex((p) => {
                    return p.type === SpecificationContentType.EmbodimentConclusion;
                });
                if (conclusionIndex >= 0) {
                    subSections.splice(conclusionIndex, 0, paragraph);
                    genContentService.updateState({
                        ...state,
                        generatedContent: {
                            ...state.generatedContent,
                            content: generatedContent.content,
                        },
                    });
                }
            } catch (error) {
                console.error('插入实施例段落失败', error);
            }
        }
    };

    return {
        configRule,
        generate,
        beforeGenerateLoading,
        modifiedOutline,
        validated,
        modified,
        comfirm,
        isAppendingMode,
    };
};
